<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import { generateSnoozeSuggestions } from 'dashboard/helper/snoozeHelpers';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'chooseTime']);

const { t, tm } = useI18n();

const headerTitle = computed(
  () => props.title || t('CONVERSATION.SCHEDULED_MESSAGE.TITLE')
);
const { resolvedLocale } = useLocale();

const searchText = ref('');

const snoozeTranslations = computed(() => {
  const raw = tm('SNOOZE_PARSER');
  if (!raw || typeof raw !== 'object') return {};
  return JSON.parse(JSON.stringify(raw));
});

const suggestions = computed(() =>
  generateSnoozeSuggestions(searchText.value, new Date(), {
    translations: snoozeTranslations.value,
    locale: resolvedLocale.value,
  })
);

const onClose = () => emit('close');

const chooseTime = suggestion => {
  if (!suggestion) return;
  emit('chooseTime', suggestion.date);
};

const onEnter = () => chooseTime(suggestions.value[0]);
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header :header-title="headerTitle" />
    <div class="w-full pt-2 px-5 pb-6">
      <input
        v-model="searchText"
        type="text"
        class="w-full reset-base text-sm px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
        :placeholder="$t('CONVERSATION.SCHEDULED_MESSAGE.PLACEHOLDER')"
        @keydown.enter.prevent="onEnter"
      />
      <ul
        v-if="suggestions.length"
        class="flex flex-col gap-1 mt-3 list-none m-0 p-0 max-h-60 overflow-y-auto"
      >
        <li v-for="suggestion in suggestions" :key="suggestion.unixTime">
          <button
            type="button"
            class="flex items-center justify-between w-full px-3 py-2 rounded-lg text-left hover:bg-n-alpha-1 text-n-slate-12"
            @click="chooseTime(suggestion)"
          >
            <span class="text-sm font-medium">{{ suggestion.label }}</span>
            <span class="text-xs text-n-slate-11">
              {{ suggestion.formattedDate }}
            </span>
          </button>
        </li>
      </ul>
      <div class="flex flex-row justify-end w-full gap-2 px-0 pt-4">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL')"
          @click="onClose"
        />
      </div>
    </div>
  </div>
</template>
